import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SearchRemoveIcon } from "@bookingcom/bui-assets-react/streamline";
import { Accordion, Button, Card, HiddenVisually, Icon, Link, List, Stack, Text, Title, useViewport } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n, Trans } from "@bookingcom/lingojs-react";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import useSearchFilters from "../../../hooks/useSearchFilters.js";
import { getExpandedSearchAirports } from "../../../store/selectors/expandedSearch.js";
import { isSirfAvailable } from "../../../store/selectors/sirf.js";
import { Sidebar } from "../Container/index.js";
import SearchBaggagePolicies from "../SearchBaggagePolicies/index.js";
import SearchFiltersSummary from "../SearchFiltersSummary/index.js";
import { SearchPaginationDesktop } from "../SearchPagination/index.js";
import { useGetAvailableFilters } from "./components/boundsFilterHooks.js";
import SearchTabs from "../SearchTabs/index.js";
import SearchSortSelector from "./components/SearchSortSelector.js";
import SirfInfoBanner from "../SirfInfoBanner/index.js";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop.js";
import { DESKTOP_SIDEBAR_WIDTH, FILTERS } from "../../../constants.js";
import useClientCachedURL from "../../../hooks/useClientCachedURL.js";
import { DestinationUkraineWarning } from "../TravelAlert/index.js";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner.js";
import { NoResultsFound } from "./components/NoResultsFound.js";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights.js";
import { TooManyFilters } from "./components/TooManyFilters.js";
import { isOfMetaOrigin } from "../../../utils/marketing-url-params.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import { getLocationParams } from "./utils.js";
import Frame from "../Frame/index.js";
import { FlightCampaignTCBanner } from "../FlightCampaignTCBanner/index.js";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { SurveyTrackerLoader } from "../../remote/SurveyTrackerFlightsUniversal.js";
import { FlexibleDateBestPriceDesktop } from "../FlexibleDateBestPrice/index.js";
import styles from "./SearchResults.desktop.module.css";
import { useFlexDatesRectrictedPOS } from "../FlexibleDateBestPrice/useFlexDatesRectrictedPOS.js";
import useProfiler from "../../../hooks/useProfiler.js";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner.js";
import { RemoteAppleCardSR, WishlistWidgetLoader } from "../../remote/index.js";
import NoSSR from "../../../app/NoSSR/index.js";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa.js";
import useOfferCartOrderData from "../../../hooks/useOfferCartOrderData.js";
import useAlternativeFaresParams from "../../../hooks/useAlternativeFaresParams.js";
import AiSummaryDesktop from "./components/AiSummary.desktop.js";
import SearchFiltersDesktop from "../SearchFilters/SearchFilters.desktop.js";
import PriceAlertToggle from "../PriceAlerts/PriceAlertToggle.js";
import { useIsPriceAlertsEligible } from "../PriceAlerts/usePriceAlertToggle.js";
import { DSATraceabilityPopover } from "../FlightDetailsInner/components/DSATraceability/DSATraceability.popover.js";
import useTrackTop5Offers from "../../../hooks/useTrackTop5Offers.js";
import { useTrackSplitBoundsStages } from "../../../utils/experiments/funnel/flights_split_bounds_us.js";
import { FilterRow } from "../SearchFiltersSummary/SearchFiltersSummary.js";
import { getFiltersMeta } from "../../../constants/filtersMeta.js";
import { useFiltersReset } from "../../../hooks/useFiltersReset.js";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters.js";
import FlightCardDesktop from "../FlightCard/FlightCard.desktop.js";
import flights_funnel_sr_gen_ai_summary_open_desktop_all_languages from "../../../utils/experiments/funnel/flights_funnel_sr_gen_ai_summary_open_desktop_all_languages.js";
import { grumble } from "../../../utils/flog.js";
import { shouldPreopenBrandedFares } from "../FlightCard/utils.js";
import { shouldShowXMSurvey } from "./utils/shouldShowXMSurvey.js";
import { getSearchResultsVisibleIndexes, getSearchResultsCampaignBanner, getSearchResultsRemoteBanners, getSearchDisplayOptions, getSearchResultsSlice } from "../../../store/searchResults/selectors.js";
import { useSelector } from "react-redux";
import { getSearch, getCabinClass } from "../../../store/search/selectors.js";
import { getFlexDateMinPriceFetchStatus } from "../../../store/flexibleDateMinPrice/selectors.js";
import FlightCardPositionAnnouncement from "./FlightCardPositionAnnouncement.js";
import SkeletonSearchSidebarDesktop from "../Skeleton/SkeletonPages/SkeletonSearchSidebar.desktop.js";
import { useTrackLayoverPriceAirportFilterWeb } from "../../../utils/experiments/funnel/flights_funnel_layover_price_airport_filter_web.js";
import flights_funnel_chip_sorting_desktop, { useTrackChipSortingDesktop } from "../../../utils/experiments/funnel/flights_funnel_chip_sorting_desktop.js";
const XM_SURVEY_MIN_RESULTS_FOR_STANDARD_POSITION = 15;
const _SearchResultsList = ({ flights, onSelectFlight }) => {
    const campaignBanner = useSelector(getSearchResultsCampaignBanner);
    const remoteBanners = useSelector(getSearchResultsRemoteBanners);
    const displayOptions = useSelector(getSearchDisplayOptions);
    const cabinClass = useSelector(getCabinClass);
    const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
    const params = new URLSearchParams(location.search);
    const currentSearchResultsPageNumber = params.get("page");
    useTrackSplitBoundsStages();
    const isFirstSearchResultsPage = currentSearchResultsPageNumber ? Number(currentSearchResultsPageNumber) === 1 : true;
    useEffect(() => {
        flights_apex_web_ancillaries_micro_conversion_aa.trackSearchResultsStages(flights);
    }, [flights]);
    useEffect(() => {
        if (displayOptions?.brandedFaresShownByDefault && flights.some(shouldPreopenBrandedFares)) {
            grumble("Search result has Delta Airlines and display option brandedFaresShownByDefault.");
        }
    }, [flights, displayOptions]);
    const shouldShowFlexDates = !displayOptions?.hideFlexiblePricesBanner &&
        flights.length >= 5 &&
        cabinClass === "ECONOMY" &&
        isFirstSearchResultsPage &&
        !isRestricedFlexDatePOS &&
        !isOfMetaOrigin();
    return (React.createElement("div", null,
        React.createElement(List, { attributes: { style: { padding: 0 } } }, flights.map((flight, index) => {
            return (React.createElement("div", { key: `${index}_${flight.token}` },
                campaignBanner && index === campaignBanner.position ? (React.createElement(Frame, { mt: 2, mb: 4 },
                    React.createElement(FlightCampaignTCBanner, { campaignBanner: campaignBanner }))) : null,
                Array.isArray(remoteBanners) &&
                    remoteBanners.includes("APPLE_CARD_BANNER_SR") &&
                    index === flights.length - 1 ? (React.createElement(RemoteAppleCardSR, null)) : undefined,
                React.createElement(FlightCardPositionAnnouncement, { index: index, total: flights.length }),
                React.createElement("div", { id: `flightcard-${index}`, "data-testid": "searchresults_card" },
                    React.createElement(FlightCardDesktop, { flight: flight, index: index, onClick: onSelectFlight })),
                shouldShowFlexDates && index === 0 ? React.createElement(FlexibleDateBestPriceDesktop, null) : null,
                campaignBanner &&
                    campaignBanner.position >= flights.length &&
                    index >= flights.length - 1 ? (React.createElement(Frame, { mt: 2, mb: 4 },
                    React.createElement(FlightCampaignTCBanner, { campaignBanner: campaignBanner }))) : null,
                isFirstSearchResultsPage &&
                    shouldShowXMSurvey(index, flights.length, XM_SURVEY_MIN_RESULTS_FOR_STANDARD_POSITION) ? (React.createElement(SurveyTrackerLoader, null)) : null));
        }))));
};
const SearchResultsList = React.memo(_SearchResultsList);
const attributes = { style: { width: `calc(100% - ${DESKTOP_SIDEBAR_WIDTH}px)` } };
const attributesRes = { style: { width: "100%", padding: 0 } };
const SearchResultsWithSidebarAndTabs = ({ children, humanRightBanner }) => {
    const [displayedFiltersCount, setDisplayedFiltersCount] = useState(3);
    const i18n = useI18n();
    const { isLarge } = useViewport();
    const { isAmerican, isEnglish } = useProfiler();
    const appliedFilters = useAppliedFilters();
    const filtersCount = appliedFilters.length;
    const filtersMeta = getFiltersMeta(i18n);
    flights_funnel_sr_gen_ai_summary_open_desktop_all_languages.useTrackStages();
    const shouldShowAiSummary = useMemo(() => {
        const inVariant = !!flights_funnel_sr_gen_ai_summary_open_desktop_all_languages.getVariant();
        return inVariant ? filtersCount === 0 : isEnglish() && !isAmerican();
    }, [isAmerican, isEnglish, filtersCount]);
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    const trackLayoverPriceAirportFilterWeb = useTrackLayoverPriceAirportFilterWeb();
    const isPriceAlertsEligible = useIsPriceAlertsEligible();
    const filtersArea = useMemo(() => {
        return (React.createElement(React.Fragment, null,
            isLarge ? shouldShowAiSummary ? React.createElement(AiSummaryDesktop, null) : null : null,
            isPriceAlertsEligible && isLarge && (React.createElement(Frame, { pb: 4 },
                React.createElement(PriceAlertToggle, null))),
            isLarge ? (React.createElement(Frame, { pb: 3 }, !!searchResultsVisibleIndexes ? (React.createElement(SkeletonSearchSidebarDesktop, null)) : (React.createElement(SearchFiltersSummary, { isCompact: !!trackLayoverPriceAirportFilterWeb() })))) : null,
            trackLayoverPriceAirportFilterWeb() ? (React.createElement(Stack, { gap: 2 },
                React.createElement(SearchFiltersDesktop, null),
                React.createElement(DSATraceabilityPopover, null))) : !!searchResultsVisibleIndexes ? undefined : (React.createElement(React.Fragment, null,
                React.createElement(SearchFiltersDesktop, null),
                React.createElement(DSATraceabilityPopover, null)))));
    }, [
        isLarge,
        isPriceAlertsEligible,
        searchResultsVisibleIndexes,
        shouldShowAiSummary,
        trackLayoverPriceAirportFilterWeb
    ]);
    const { resetFilter, resetAllFilters } = useFiltersReset();
    return (React.createElement("div", { className: styles.searchResults, "data-testid": "searchresults_list" },
        React.createElement(NoSSR, null,
            React.createElement(Frame, { direction: "row" },
                isLarge ? React.createElement(Sidebar, null, filtersArea) : null,
                React.createElement(Frame, { direction: "column", grow: 1, pb: 4, pl: 6, attributes: isLarge ? attributes : attributesRes },
                    React.createElement(HiddenVisually, null,
                        React.createElement(Text, { tagName: "h2" }, i18n.trans(t("a11y_flights_sr__results_hidden_heading")))),
                    isLarge ? null : shouldShowAiSummary ? React.createElement(AiSummaryDesktop, null) : null,
                    isPriceAlertsEligible && !isLarge && (React.createElement(Frame, { pb: 4 },
                        React.createElement(PriceAlertToggle, null))),
                    React.createElement(SearchResultBanner, { banner: humanRightBanner }),
                    !isLarge ? (React.createElement(Card, { className: styles.filtersAccordion },
                        React.createElement(Accordion, { titleContent: React.createElement(SearchFiltersSummary, { isCompact: true, setDisplayedFiltersCountProp: setDisplayedFiltersCount }) },
                            React.createElement(React.Fragment, null,
                                React.createElement("div", { className: styles.appliedFilters },
                                    filtersCount > 0 && (React.createElement(Link, { onClick: resetAllFilters, attributes: { "data-testid": "search_filters_summary_reset_all" } },
                                        React.createElement(Trans, { tag: "flights_filters_reset_all_action" }))),
                                    appliedFilters.slice(0, displayedFiltersCount).map((filter, index) => (React.createElement(Frame, { key: index, mb: 3 },
                                        React.createElement(FilterRow, { filterMeta: filtersMeta[filter], resetFilter: resetFilter }))))),
                                filtersArea)))) : null,
                    React.createElement(SearchResultsTabsContainer, null, children))))));
};
const SearchResultsTabsContainer = (props) => {
    const i18n = useI18n();
    const { filteredTotalCount = 0 } = useGetAvailableFilters();
    const { isSmall } = useViewport();
    useTrackChipSortingDesktop();
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    return (React.createElement(React.Fragment, null, flights_funnel_chip_sorting_desktop.trackWithDefaultStage() ? (React.createElement(Stack, { gap: 4 },
        React.createElement(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center" },
            searchResultsVisibleIndexes ? (React.createElement("div", null)) : (React.createElement(Title, { title: i18n.trans(t("flights_sr_sort_chips_title", {
                    variables: {
                        num_flights: filteredTotalCount
                    },
                    num_exception: filteredTotalCount
                })), variant: "strong_2", titleTagName: "h2" })),
            React.createElement(SearchSortSelector, { isSmall: isSmall })),
        React.createElement(Stack.Item, null, props.children))) : (React.createElement(SearchTabs, { showBestRankingInfo: true }, props.children))));
};
const FailedSearchResults = ({ expandToCitySearch, i18n }) => {
    const search = useSelector(getSearch);
    return (React.createElement(Frame, { mt: 6, mb: 6, attributes: { style: { minHeight: "40vh" } } },
        React.createElement(Frame, { m: 3, alignItems: "center" },
            React.createElement(Icon, { svg: SearchRemoveIcon, size: "small", className: styles.searchRemoveIcon }),
            React.createElement(Text, { variant: "headline_2" }, i18n.trans(t("flights_search_no_results_header")))),
        React.createElement(Frame, { alignItems: "center" },
            React.createElement(Text, { variant: "body_2" },
                i18n.trans(t("flights_search_no_results_copy_desktop")),
                " ")),
        search.type !== "MULTISTOP" && (React.createElement(Frame, { alignItems: "center", m: 6 },
            React.createElement(Button, { onClick: expandToCitySearch }, i18n.trans(t("flights_search_no_results_nearbyairports_cta")))))));
};
const SearchResultsDesktopSection = ({ airports, onSelectFlight, hasAppliedFilters }) => {
    const history = useHistory();
    const location = useLocation();
    const { createUrl } = useCreateUrl();
    const { applyFilters } = useSearchFilters();
    const search = useSelector(getSearch);
    const searchResults = useSelector(getSearchResultsSlice);
    const { flights = [], subsidizedFaresSummary } = searchResults;
    const i18n = useI18n();
    const { cacheLastSuccessfulSearchUrl } = useClientCachedURL();
    const offerCartOrderData = useOfferCartOrderData();
    const { trackTop5OffersClick } = useTrackTop5Offers();
    const { setAlternativeFaresParams } = useAlternativeFaresParams();
    const { isLoggedInAuthLow } = useProfiler();
    const { isLarge } = useViewport();
    useEffect(() => {
        applyFilters(search.filtersDraft);
    }, [search.filtersDraft, applyFilters]);
    const showFlightDetails = useCallback(async (flight) => {
        trackTop5OffersClick(flight.token);
        onSelectFlight();
        offerCartOrderData.set(flight.token, {
            offer: flight.token,
            cabinClass: searchResults.searchCriteria?.cabinClass
        });
        const url = createUrl(`/flights/${airports}/${flight.token}/?${getLocationParams(location.search)}`);
        history.push(url);
    }, [
        trackTop5OffersClick,
        onSelectFlight,
        offerCartOrderData,
        searchResults.searchCriteria?.cabinClass,
        createUrl,
        airports,
        location.search,
        history
    ]);
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    cacheLastSuccessfulSearchUrl(airports);
    searchResults.fetchedAt && setAlternativeFaresParams("searchedAt", searchResults.fetchedAt);
    return (React.createElement(React.Fragment, null,
        React.createElement(NoDirectFlightsBanner, null),
        React.createElement(DestinationUkraineWarning, null),
        isSirfAvailable(subsidizedFaresSummary) && (React.createElement(SirfInfoBanner, { bannerName: "search-results", bannerContent: i18n.trans(t("flights_sr_spanish_discount_banner")) })),
        React.createElement(SearchBaggagePolicies, null),
        React.createElement(SearchResultsList, { flights: searchResultsVisibleIndexes
                ? flights.filter((_, index) => searchResultsVisibleIndexes.includes(index))
                : flights, onSelectFlight: showFlightDetails, hasAppliedFilters: hasAppliedFilters }),
        !!searchResultsVisibleIndexes ? undefined : (React.createElement("div", { className: !isLarge ? styles.paginationRes : "" },
            React.createElement(SearchPaginationDesktop, null))),
        isLoggedInAuthLow() && !isFeatureRunningClientSide("FLIGHTS_WEB_WISHLIST_WIDGET_DESKTOP_SSR") ? (React.createElement(WishlistWidgetLoader, null)) : undefined));
};
const ComposedSearchResults = ({ airports, onSelectFlight }) => {
    const search = useSelector(getSearch);
    const searchResults = useSelector(getSearchResultsSlice);
    const flexibleDatePriceFetchStatus = useSelector(getFlexDateMinPriceFetchStatus);
    const cabinClass = useSelector(getCabinClass);
    const history = useHistory();
    const location = useLocation();
    const { createUrl } = useCreateUrl();
    const { applyFilters } = useSearchFilters();
    const { requestId } = useGlobalContext();
    const trackV2 = useEventTracking("search_results", requestId);
    const { flights = [], fetchStatus, error, availableFilters, banners } = searchResults;
    const appliedFilters = useAppliedFilters();
    const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
    const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");
    useEffect(() => {
        applyFilters(search.filtersDraft);
    }, [search.filtersDraft, applyFilters]);
    const i18n = useI18n();
    const expandedSearchAirports = useMemo(() => {
        const { searchSegments } = search;
        const { from, to } = searchSegments[0];
        return getExpandedSearchAirports(from, to);
    }, [search]);
    const expandToCitySearch = useCallback(() => {
        if (expandedSearchAirports) {
            const { fromCities, toCities, fromLocationName, toLocationName } = expandedSearchAirports;
            const params = new URLSearchParams(location.search);
            params.set("from", fromCities);
            params.set("to", toCities);
            params.set("fromLocationName", fromLocationName);
            params.set("toLocationName", toLocationName);
            history.replace(createUrl(`/flights/${fromCities}-${toCities}/?${params}`));
            window.location.reload();
        }
    }, [history, location, expandedSearchAirports, createUrl]);
    useEffect(() => {
        if (["loading", "initial"].includes(fetchStatus))
            return;
        if (flights.length === 0)
            trackV2("show_empty_search_results");
    }, [flights, fetchStatus, trackV2]);
    const firstLoad = !availableFilters.durationMin;
    const noFlightsFound = (error && error["code"] === "SEARCH_SEARCHFLIGHTS_NO_FLIGHTS_FOUND") || flights.length === 0;
    const canExtendSearch = appliedFilters.length === 0 && noFlightsFound && expandedSearchAirports;
    const canRemoveDirectFlightsFilter = appliedFilters.includes(FILTERS.stops) && noFlightsFound;
    let resultingComp = undefined;
    const flexibleFetchConditionalStatus = useMemo(() => {
        if (cabinClass === "ECONOMY" && !isRestricedFlexDatePOS && !isOfMetaOrigin()) {
            return {
                loading: flexibleDatePriceFetchStatus === "initial" || flexibleDatePriceFetchStatus === "loading",
                success: flexibleDatePriceFetchStatus === "success"
            };
        }
        return {
            loading: false,
            success: true
        };
    }, [flexibleDatePriceFetchStatus, isRestricedFlexDatePOS, cabinClass]);
    if (fetchStatus === "initial" || fetchStatus === "loading" || flexibleFetchConditionalStatus.loading) {
        if (firstLoad) {
            return React.createElement(SkeletonSearchDesktop, null);
        }
        resultingComp = React.createElement(SkeletonSearchContent, null);
    }
    if (fetchStatus === "failed" && canExtendSearch) {
        return React.createElement(FailedSearchResults, { expandToCitySearch: expandToCitySearch, i18n: i18n });
    }
    if (fetchStatus === "success") {
        if (flights.length > 0) {
            resultingComp = (React.createElement(SearchResultsDesktopSection, { airports: airports, onSelectFlight: onSelectFlight, hasAppliedFilters: appliedFilters.length > 0 }));
        }
        else if (canRemoveDirectFlightsFilter) {
            return (React.createElement(React.Fragment, null,
                React.createElement(SearchResultBanner, { banner: humanRightBanner }),
                React.createElement(RemoveDirectFlights, null)));
        }
    }
    if (!resultingComp) {
        if (appliedFilters.length > 0) {
            resultingComp = React.createElement(TooManyFilters, null);
        }
        else {
            return React.createElement(NoResultsFound, null);
        }
    }
    const screenReaderText = () => {
        if (appliedFilters.length > 0 && flights) {
            return i18n.trans(t("a11y_flights_filters_general_updated"));
        }
        if (appliedFilters.length === 0 && flights) {
            return i18n.trans(t("a11y_flights_sr_results_loaded"));
        }
        return "";
    };
    return (React.createElement(SearchResultsWithSidebarAndTabs, { humanRightBanner: humanRightBanner },
        resultingComp,
        React.createElement(HiddenVisually, null,
            React.createElement("div", { "aria-live": "polite" }, screenReaderText()))));
};
export default React.memo(ComposedSearchResults);
